import { setPlacementDragActive } from '@/lib/placement/placement-drag-activity'
import { useCallback, useEffect, useRef, useState } from 'react'
import type React from 'react'
import type { CenterCanvasHandle } from '@/components/app/editor/center-canvas'
import { type GridSnapLiveRect, snapPointToNearestGridCorner } from '@/lib/guides/grid-snap'
import {
  getPasteboardPlacementBounds,
  getSpreadPlacementBounds,
  isCanvasPointInBounds,
  meetsPhotoDragThreshold,
} from '@/lib/photos/photo-placement-geometry'
import { isClientPointOverPhotoDropSurface } from '@/lib/photos/photo-placement-hit-test'

/**
 * The drag-from-a-tray-and-drop-onto-the-canvas state machine shared by every "placeable tray
 * item" provider (Photos, ClikArt). The two providers used to carry near-identical copies of this
 * logic; what genuinely differs per item kind (how it is placed, how it fills a shape, what is
 * primed when a drag starts) is injected through `PlacementSessionOptions`.
 */

/** Why a hit drop target may not receive the item. Currently only text-link chain membership. */
export type PlacementDropBlockedReason = 'linked-text-frame'

/**
 * The topmost drop target under the cursor. A blocked target is still reported — it suppresses the
 * valid-target highlight and refuses the drop outright, rather than letting the item fall through
 * to standalone placement or to whatever sits behind it (WMPCM-8516).
 */
export type PlacementDropHit = {
  shapeId: string
  kindId: number
  blockedReason?: PlacementDropBlockedReason
}

export type PlacementCanvasApi = {
  toCanvasPoint: (clientX: number, clientY: number) => { canvasX: number; canvasY: number } | null
  isOverCanvas: (clientX: number, clientY: number) => boolean
  bleedWidth: number
  bleedHeight: number
  /** Returns the topmost fillable-or-blocked shape at client coords, or null. */
  hitFillableShapeAtPoint: (clientX: number, clientY: number) => PlacementDropHit | null
}

export type PlacementDragPhase = 'idle' | 'pending' | 'dragging' | 'keyboard-carry'

export type PlacementIntrinsicSize = { width: number; height: number }

/** Handed to `onDragStart` so a primer can update the in-flight item without a circular ref. */
export type PlacementItemControls<TItem> = {
  itemRef: React.MutableRefObject<TItem | null>
  setItem: (next: TItem | null) => void
}

export type PlacementActionContext = {
  api: PlacementCanvasApi
  announce: (message: string) => void
}

export type PlacementSessionOptions<TItem> = {
  centerCanvasRef: React.RefObject<CenterCanvasHandle | null>
  /** Owned by the caller (its primers write to it); the session only clears it on reset. */
  intrinsicSizeRef: React.MutableRefObject<PlacementIntrinsicSize | null>
  /**
   * Where a drop counts. `'pasteboard'` (the default) is the whole grey work area, where a photo or
   * ClikArt may be dropped beside the spread. `'spread'` is the spread's own box: over the
   * pasteboard the drag shows no drag-over state and a release cancels, for an item that is only
   * ever placed on the spread (a template).
   */
  dropArea?: 'pasteboard' | 'spread'
  snapToGrid?: boolean
  gridSnapStep?: number
  gridSnapLiveRects?: GridSnapLiveRect[]
  onScreenReaderAnnounce?: (message: string) => void
  onBlockedDrop?: (reason: PlacementDropBlockedReason) => void
  /** Spoken (and shown) when a drop is refused. */
  blockedDropMessage: string
  keyboardCenterFraction: number
  /** When set, a touch pointer must be held this long (or move past the drag threshold) to drag. */
  touchLongPressMs?: number
  displayNameOf: (item: TItem) => string
  /** Called once an item becomes the dragged item — the place to prime its natural size. */
  onDragStart?: (item: TItem, controls: PlacementItemControls<TItem>) => void
  /** Called at the end of every reset, for per-drag state only the caller knows about. */
  onReset?: () => void
  placeAtCanvas: (
    item: TItem,
    centerX: number,
    centerY: number,
    context: PlacementActionContext
  ) => void
  fillShape: (
    item: TItem,
    hit: PlacementDropHit,
    point: { canvasX: number; canvasY: number },
    context: PlacementActionContext
  ) => void
}

type PendingPointer<TItem> = {
  item: TItem
  clientX: number
  clientY: number
  thumbRect: DOMRect
  pointerId: number
}

const FLYBACK_MS = 220

export function usePlacementSession<TItem>(options: PlacementSessionOptions<TItem>) {
  // Every callback below reads the latest options through this ref, so callers can pass plain
  // inline closures without churning the identities the window listeners depend on.
  const optionsRef = useRef(options)

  optionsRef.current = options

  const canvasApiRef = useRef<PlacementCanvasApi | null>(null)
  const [phase, setPhase] = useState<PlacementDragPhase>('idle')
  const [dropTargetShapeId, setDropTargetShapeId] = useState<string | null>(null)
  const dropTargetRef = useRef<PlacementDropHit | null>(null)
  const [item, setItem] = useState<TItem | null>(null)
  const [ghostClient, setGhostClient] = useState({ x: 0, y: 0 })
  const [ghostSize, setGhostSize] = useState({ width: 80, height: 80 })
  const [thumbRect, setThumbRect] = useState<DOMRect | null>(null)
  const [overCanvas, setOverCanvas] = useState(false)
  const [flybackActive, setFlybackActive] = useState(false)

  const phaseRef = useRef<PlacementDragPhase>('idle')
  const itemRef = useRef<TItem | null>(null)
  const pointerIdRef = useRef<number | null>(null)
  const pointerCaptureElRef = useRef<HTMLElement | null>(null)
  const startClientRef = useRef({ x: 0, y: 0 })
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rafRef = useRef<number | null>(null)
  const pendingPointerRef = useRef<PendingPointer<TItem> | null>(null)

  const announce = useCallback((message: string) => {
    optionsRef.current.onScreenReaderAnnounce?.(message)
  }, [])

  const registerCanvasApi = useCallback((api: PlacementCanvasApi | null) => {
    canvasApiRef.current = api
  }, [])

  const clearLongPress = useCallback(() => {
    if (longPressTimerRef.current != null) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }, [])

  const setPhaseSync = useCallback((next: PlacementDragPhase) => {
    phaseRef.current = next
    setPhase(next)
    // A pointer drag clears the canvas selection on its way in; listeners that react to an emptied
    // selection use this to tell that from a deselect by the user (WMPCM-13378). A keyboard carry
    // leaves the selection alone, so it does not count.
    setPlacementDragActive(next === 'pending' || next === 'dragging')
  }, [])

  const setItemSync = useCallback((next: TItem | null) => {
    itemRef.current = next
    setItem(next)
  }, [])

  const resetDrag = useCallback(() => {
    clearLongPress()
    pendingPointerRef.current = null
    const activePointerId = pointerIdRef.current

    pointerIdRef.current = null

    const captureEl = pointerCaptureElRef.current

    if (captureEl != null && activePointerId != null) {
      try {
        if (captureEl.hasPointerCapture(activePointerId)) {
          captureEl.releasePointerCapture(activePointerId)
        }
      } catch {
        // Pointer may already be released.
      }
    }

    pointerCaptureElRef.current = null
    dropTargetRef.current = null
    optionsRef.current.intrinsicSizeRef.current = null
    setFlybackActive(false)
    setPhaseSync('idle')
    setItemSync(null)
    setThumbRect(null)
    setOverCanvas(false)
    setDropTargetShapeId(null)
    optionsRef.current.onReset?.()
  }, [clearLongPress, setItemSync, setPhaseSync])

  const snapCenter = useCallback(
    (canvasX: number, canvasY: number): { canvasX: number; canvasY: number } => {
      const { snapToGrid = false, gridSnapStep = 12, gridSnapLiveRects = [] } = optionsRef.current

      if (!snapToGrid || gridSnapLiveRects.length === 0) {
        return { canvasX, canvasY }
      }

      const step = Math.max(1, Math.round(Number(gridSnapStep)) || 1)
      const snapped = snapPointToNearestGridCorner(canvasX, canvasY, step, gridSnapLiveRects)

      return { canvasX: snapped.x, canvasY: snapped.y }
    },
    []
  )

  const cancelDrag = useCallback(() => {
    if (phaseRef.current === 'idle') return
    announce('Drag cancelled')
    resetDrag()
  }, [announce, resetDrag])

  const beginDraggingItem = useCallback(
    (targetItem: TItem, rect: DOMRect) => {
      setItemSync(targetItem)
      setThumbRect(rect)
      setGhostSize({ width: rect.width, height: rect.height })
    },
    [setItemSync]
  )

  const announceAndPrime = useCallback(
    (targetItem: TItem) => {
      announce(`Dragging ${optionsRef.current.displayNameOf(targetItem)}`)
      optionsRef.current.onDragStart?.(targetItem, { itemRef, setItem: setItemSync })
    },
    [announce, setItemSync]
  )

  const startDragging = useCallback(
    (targetItem: TItem, rect: DOMRect, clientX: number, clientY: number, pid: number) => {
      beginDraggingItem(targetItem, rect)
      setGhostClient({ x: clientX, y: clientY })
      setPhaseSync('dragging')
      pointerIdRef.current = pid
      announceAndPrime(targetItem)
    },
    [announceAndPrime, beginDraggingItem, setPhaseSync]
  )

  const beginPointerDrag = useCallback(
    (
      targetItem: TItem,
      clientX: number,
      clientY: number,
      rect: DOMRect,
      pointerId: number,
      pointerType = 'mouse',
      pointerCaptureEl: HTMLElement | null = null
    ) => {
      if (phaseRef.current !== 'idle') return
      // Not an edit, so no Undo step: a recorded deselect becomes an Undo press that only re-selects.
      optionsRef.current.centerCanvasRef.current?.clearSelectionWithoutHistory?.()

      if (pointerCaptureEl) {
        try {
          pointerCaptureEl.setPointerCapture(pointerId)
          pointerCaptureElRef.current = pointerCaptureEl
        } catch {
          pointerCaptureElRef.current = null
        }
      }

      const longPressMs = optionsRef.current.touchLongPressMs

      if (pointerType !== 'touch' || longPressMs == null) {
        startDragging(targetItem, rect, clientX, clientY, pointerId)
        return
      }

      startClientRef.current = { x: clientX, y: clientY }
      pendingPointerRef.current = {
        item: targetItem,
        clientX,
        clientY,
        thumbRect: rect,
        pointerId,
      }
      setPhaseSync('pending')

      clearLongPress()
      longPressTimerRef.current = setTimeout(() => {
        const pending = pendingPointerRef.current

        if (!pending || phaseRef.current !== 'pending') return
        startDragging(
          pending.item,
          pending.thumbRect,
          pending.clientX,
          pending.clientY,
          pending.pointerId
        )
        pendingPointerRef.current = null
      }, longPressMs)
    },
    [clearLongPress, setPhaseSync, startDragging]
  )

  const beginKeyboardCarry = useCallback(
    (targetItem: TItem, rect: DOMRect) => {
      if (phaseRef.current !== 'idle') return
      beginDraggingItem(targetItem, rect)
      setPhaseSync('keyboard-carry')
      announceAndPrime(targetItem)
    },
    [announceAndPrime, beginDraggingItem, setPhaseSync]
  )

  const isPointerOverDropSurface = useCallback((clientX: number, clientY: number): boolean => {
    const api = canvasApiRef.current

    if (!api?.isOverCanvas(clientX, clientY)) return false
    if (!isClientPointOverPhotoDropSurface(clientX, clientY)) return false
    if (optionsRef.current.dropArea !== 'spread') return true

    const pt = api.toCanvasPoint(clientX, clientY)

    return (
      pt != null &&
      isCanvasPointInBounds(
        pt.canvasX,
        pt.canvasY,
        getSpreadPlacementBounds(api.bleedWidth, api.bleedHeight)
      )
    )
  }, [])

  const updateGhostFromClient = useCallback(
    (clientX: number, clientY: number) => {
      if (rafRef.current != null) return
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null
        setGhostClient({ x: clientX, y: clientY })

        const isOver = isPointerOverDropSurface(clientX, clientY)

        setOverCanvas(isOver)

        const hit = isOver
          ? (canvasApiRef.current?.hitFillableShapeAtPoint(clientX, clientY) ?? null)
          : null

        // Keep the blocked hit in the ref — `finishDrop` needs it to refuse the drop — but keep it
        // out of the highlight state, so the ghost never turns green over a frame that will
        // reject the item (WMPCM-8516).
        dropTargetRef.current = hit
        setDropTargetShapeId(hit && !hit.blockedReason ? hit.shapeId : null)
      })
    },
    [isPointerOverDropSurface]
  )

  const cancelWithFlyback = useCallback(() => {
    announce('Drag cancelled')
    setFlybackActive(true)
    window.setTimeout(() => {
      setFlybackActive(false)
      resetDrag()
    }, FLYBACK_MS)
  }, [announce, resetDrag])

  const finishDrop = useCallback(
    (clientX: number, clientY: number) => {
      const api = canvasApiRef.current
      const activeItem = itemRef.current

      if (!api || !activeItem) {
        if (!api) announce('Drag cancelled')
        resetDrag()
        return
      }

      if (!isPointerOverDropSurface(clientX, clientY)) {
        cancelWithFlyback()
        return
      }

      const pt = api.toCanvasPoint(clientX, clientY)
      const placementBounds = getPasteboardPlacementBounds(api.bleedWidth, api.bleedHeight)

      if (!pt || !isCanvasPointInBounds(pt.canvasX, pt.canvasY, placementBounds)) {
        cancelWithFlyback()
        return
      }

      const dropTarget = dropTargetRef.current
      const context = { api, announce }

      // A blocked target refuses the item outright — deliberately *before* the standalone
      // fallback below, so nothing at all is placed (WMPCM-8516). Falling through would drop a
      // standalone item on top of the linked frame, which is the locked-shape behaviour and not
      // what this guard is for.
      if (dropTarget?.blockedReason) {
        optionsRef.current.onBlockedDrop?.(dropTarget.blockedReason)
        // The same constant the toast uses, not a hand-written copy: the wording deliberately names
        // the one action that unblocks the drop ("Unlink it first."), so the sighted and the
        // screen-reader user get the same remedy.
        announce(optionsRef.current.blockedDropMessage)
        resetDrag()
        return
      }

      if (dropTarget) {
        optionsRef.current.fillShape(
          activeItem,
          dropTarget,
          { canvasX: pt.canvasX, canvasY: pt.canvasY },
          context
        )
        resetDrag()
        return
      }

      const snapped = snapCenter(pt.canvasX, pt.canvasY)

      optionsRef.current.placeAtCanvas(activeItem, snapped.canvasX, snapped.canvasY, context)
      resetDrag()
    },
    [announce, cancelWithFlyback, isPointerOverDropSurface, resetDrag, snapCenter]
  )

  const placeKeyboardCarry = useCallback(() => {
    const api = canvasApiRef.current
    const activeItem = itemRef.current

    if (!api || !activeItem || phaseRef.current !== 'keyboard-carry') return

    const fraction = optionsRef.current.keyboardCenterFraction
    const snapped = snapCenter(api.bleedWidth * fraction, api.bleedHeight * fraction)

    optionsRef.current.placeAtCanvas(activeItem, snapped.canvasX, snapped.canvasY, {
      api,
      announce,
    })
    resetDrag()
  }, [announce, resetDrag, snapCenter])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const currentPhase = phaseRef.current

      if (currentPhase === 'idle') return

      if (e.key === 'Escape') {
        // Capture phase so Escape cancels the drag before Radix Tooltip/DismissableLayer (or
        // other capture listeners) consume it — same pattern as node-edit Escape in center-canvas.
        // Without this, Escape does nothing while the ghost stays up.
        e.preventDefault()
        e.stopPropagation()
        cancelDrag()
        return
      }

      if (currentPhase === 'keyboard-carry' && e.key === 'Enter') {
        e.preventDefault()
        placeKeyboardCarry()
      }
    }

    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [cancelDrag, placeKeyboardCarry])

  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      const currentPhase = phaseRef.current

      if (currentPhase === 'pending' && pendingPointerRef.current) {
        const dx = e.clientX - startClientRef.current.x
        const dy = e.clientY - startClientRef.current.y

        if (meetsPhotoDragThreshold(dx, dy)) {
          clearLongPress()
          const pending = pendingPointerRef.current

          startDragging(pending.item, pending.thumbRect, e.clientX, e.clientY, pending.pointerId)
          pendingPointerRef.current = null
          updateGhostFromClient(e.clientX, e.clientY)
        }

        return
      }

      if (currentPhase === 'dragging' && pointerIdRef.current === e.pointerId) {
        updateGhostFromClient(e.clientX, e.clientY)
      }
    }

    const onPointerUp = (e: PointerEvent) => {
      const currentPhase = phaseRef.current

      if (currentPhase === 'pending') {
        clearLongPress()
        pendingPointerRef.current = null
        resetDrag()
        return
      }

      if (currentPhase === 'dragging' && pointerIdRef.current === e.pointerId) {
        finishDrop(e.clientX, e.clientY)
      }
    }

    const onPointerCancel = (e: PointerEvent) => {
      if (phaseRef.current === 'dragging' && pointerIdRef.current === e.pointerId) {
        cancelDrag()
      }
    }

    window.addEventListener('pointermove', onPointerMove, true)
    window.addEventListener('pointerup', onPointerUp, true)
    window.addEventListener('pointercancel', onPointerCancel, true)

    return () => {
      window.removeEventListener('pointermove', onPointerMove, true)
      window.removeEventListener('pointerup', onPointerUp, true)
      window.removeEventListener('pointercancel', onPointerCancel, true)
    }
  }, [cancelDrag, clearLongPress, finishDrop, resetDrag, startDragging, updateGhostFromClient])

  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      clearLongPress()
      // A session that unmounts mid-drag would otherwise leave the drag looking active.
      setPlacementDragActive(false)
    }
  }, [clearLongPress])

  const isActive = phase === 'dragging' || phase === 'keyboard-carry'
  // Everything the ghost component needs besides the item itself and its opacity; null when no
  // ghost should render (a flyback keeps it up briefly after the drag has ended).
  const ghost =
    item != null && thumbRect != null && (isActive || flybackActive)
      ? {
          clientX: ghostClient.x,
          clientY: ghostClient.y,
          width: ghostSize.width,
          height: ghostSize.height,
          overCanvas,
          overFillTarget: dropTargetShapeId != null,
          keyboardCarry: phase === 'keyboard-carry',
          flyback: flybackActive,
          thumbOrigin: thumbRect,
        }
      : null

  return {
    item,
    isActive,
    isKeyboardCarry: phase === 'keyboard-carry',
    dropTargetShapeId,
    registerCanvasApi,
    beginPointerDrag,
    beginKeyboardCarry,
    cancelDrag,
    ghost,
  }
}
